import {useCallback, useMemo, useRef, useState} from "react";
import {fetchApi} from "@root/server/client/esweb/src/lib/fetchApi.ts";
import Debug from 'debug';
import {TurnstileInstance} from "@marsidev/react-turnstile";
import {t} from "@root/server/client/esweb/src/lib/i18n.ts";
import {getClientConfig} from "@root/server/client/esweb/src/lib/config.ts";

const debug = Debug('app:userFormSubmission');

interface UseFormSubmissionOptions<TValues> {
    onSubmit?: (values: TValues) => Promise<unknown> | unknown;
    submitUrl?: string;
    method?: string;
    requireTurnstile?: boolean;
    /**
     * Allows transforming a thrown value into an array of error messages.
     * Defaults to deriving a message from the error.
     */
    parseErrorMessages?: (error: unknown) => string[];
}

export interface UseFormSubmissionResult<TValues> {
    execute: (values: TValues) => Promise<void>;
    isSubmitting: boolean;
    canSubmit: boolean;
    turnstileProps: {onSuccess: (token: string) => void, onError: (e: string) => void, onExpire: () => void, siteKey: string}
    serverErrors: string[];
    resetErrors: () => void;
    setServerErrors: (messages: string[]) => void;
}

const defaultParseErrors = (error: unknown): string[] => {
    if (Array.isArray(error)) {
        return error.map((item) => `${item}`);
    }

    if (error instanceof Error) {
        return [error.message];
    }

    if (typeof error === "string") {
        return [error];
    }

    return [t({cs: 'Došlo k neznámé chybě.', en: 'An unknown error occurred.'})];
};

const config = getClientConfig();

export function useFormSubmission<TValues>(options: UseFormSubmissionOptions<TValues>): UseFormSubmissionResult<TValues> {
    const {onSubmit, submitUrl, method = 'POST', requireTurnstile = false, parseErrorMessages = defaultParseErrors} = options;

    if (!onSubmit && !submitUrl) {
        throw new Error('Missing onSubmit or submitUrl');
    }

    const [canSubmit, setCanSubmit] = useState(!requireTurnstile);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const extraHeadersRef = useRef<{'cf-turnstile-response'?: string}>({});
    const turnstileRef = useRef<TurnstileInstance | null>(null);

    const turnstileProps = useMemo(() => ({
        onSuccess (token) {
            debug('setting turnstile token', token)
            extraHeadersRef.current['cf-turnstile-response'] = token;
            setCanSubmit(true);
        },
        onError(e) {
            console.error(e);
            delete extraHeadersRef.current['cf-turnstile-response'];
            setCanSubmit(false);
        },
        onExpire(){
            console.warn('turnstile expired');
            delete extraHeadersRef.current['cf-turnstile-response'];
            setCanSubmit(false);
        },
        siteKey: config.CF_SITE_KEY,
        ref: turnstileRef
    }), []);

    const resetErrors = useCallback(() => {
        setServerErrors([]);
    }, []);

    const execute = useCallback(async (values: TValues) => {
        debug('execute called with ', values);
        setIsSubmitting(true);
        setServerErrors([]);

        try {
            if(onSubmit) {
                await onSubmit(values);
            }
            if (submitUrl) {
                await fetchApi(submitUrl, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        ...extraHeadersRef.current,
                    },
                    body: JSON.stringify(values),
                });
            }
        } catch (error) {
            console.error(error);
            setServerErrors(parseErrorMessages(error));
            if(requireTurnstile) {
                turnstileProps.onExpire();
                turnstileRef.current?.reset();
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [onSubmit, parseErrorMessages, submitUrl]);

    return {
        execute,
        isSubmitting,
        canSubmit,
        serverErrors,
        resetErrors,
        setServerErrors,
        turnstileProps
    };
}
